/**
 * Transporte WebTransport del cliente (netcode competitivo, "tiempo real").
 *
 * WebTransport = UDP nativo del navegador (HTTP/3 / QUIC). A diferencia del
 * WebSocket (TCP, con head-of-line blocking bajo pérdida), usa **datagramas no
 * confiables**: manda el estado más nuevo y descarta lo viejo, sin reenvíos que
 * bloqueen. Es el transporte de los juegos rápidos competitivos.
 *
 * Emparejado con el netcode UDP-ready de `@tak/sim`: wire binario (`codec`),
 * redundancia de inputs y descarte de snapshots obsoletos (ya en PredictiveClient).
 *
 * NOTA DE VERIFICACIÓN: el handshake QUIC requiere HTTP/3 + TLS y solo se valida
 * de extremo a extremo en un despliegue HTTPS real (no en tests headless locales).
 * El WebSocket (`wsTransport.ts`) queda como fallback probado.
 */
import {
  PredictiveClient,
  ClientOptions,
  InputState,
  InputCommand,
  encodeInputPacket,
  decodeSnapshot,
} from "@tak/sim";

const INPUT_REDUNDANCY = 5; // reenvía los últimos 5 inputs por datagrama

export class WtNetClient {
  readonly client: PredictiveClient;
  private readonly transport: WebTransport;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private readonly recent: InputCommand[] = [];
  private open = false;
  /** MVP: 1 humano (slot 0) + bot server-side; el matchmaking 2 humanos llega con M3. */
  localSlot: 0 | 1 = 0;
  onReady: (() => void) | null = null;

  constructor(url: string, opts: ClientOptions = { players: 2, localSlot: 0 }) {
    // autoLead: red real (UDP/QUIC) → el cliente lidera al servidor para que sus
    // inputs no lleguen tarde y se descarten (ver PredictiveClient).
    this.client = new PredictiveClient({ ...opts, autoLead: true });
    this.transport = new WebTransport(url);
    void this.init();
  }

  private async init(): Promise<void> {
    try {
      await this.transport.ready;
      this.writer = this.transport.datagrams.writable.getWriter();
      this.open = true;
      this.onReady?.();
      void this.readLoop();
    } catch {
      this.open = false; // sin conexión: el cliente sigue prediciendo local
    }
  }

  private async readLoop(): Promise<void> {
    const reader = this.transport.datagrams.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value && value.byteLength >= 14) this.client.reconcile(decodeSnapshot(value));
      }
    } catch {
      this.open = false;
    }
  }

  get connected(): boolean {
    return this.open;
  }

  /** Predice localmente y envía los últimos K inputs (redundancia) como datagrama. */
  send(input: InputState): void {
    const cmd = this.client.predict(input);
    this.recent.push(cmd);
    while (this.recent.length > INPUT_REDUNDANCY) this.recent.shift();
    if (this.open && this.writer) {
      void this.writer.write(encodeInputPacket(this.recent)).catch(() => { this.open = false; });
    }
  }

  close(): void {
    this.open = false;
    try { this.transport.close(); } catch { /* ya cerrado */ }
  }
}
